import { useTranslation } from 'react-i18next'

import emptyBoxCombinedUrl from '@/assets/user-apps/empty-box-combined.svg'
import emptyBoxShapeUrl from '@/assets/user-apps/empty-box-shape.svg'
import emptyOvalUrl from '@/assets/user-apps/empty-oval.png'
import pinFilledIconUrl from '@/assets/user-apps/pin-filled.svg'
import pinOutlineIconUrl from '@/assets/user-apps/pin-outline.svg'
import { Badge } from '@/components/data-display/badge'
import { ErrorState } from '@/components/feedback/error-state'
import { Skeleton } from '@/components/feedback/skeleton'
import { SearchInput } from '@/components/forms/search-input'
import { Modal } from '@/components/overlays/modal'
import { Button } from '@/components/primitives/button'
import { UserShellPage } from '@/features/user/shell/shell-page'
import { cn } from '@/lib/cn'
import { useMediaQuery } from '@/lib/use-media-query'

import type { UserApp } from './api'
import { AppsMobilePage } from './mobile/apps-mobile-page'
import { appErrorCopy, useAppsPage } from './use-apps-page'

function PinIcon({ filled = false }: { filled?: boolean }) {
  return (
    <img
      alt=""
      aria-hidden
      className="size-3.5"
      src={filled ? pinFilledIconUrl : pinOutlineIconUrl}
    />
  )
}

function AppAvatar({ app, size = 'sm' }: { app: UserApp; size?: 'sm' | 'lg' }) {
  const initial = Array.from(app.name.trim())[0]?.toUpperCase() ?? '?'
  const avatarClassName = cn(
    'shrink-0 bg-[#e2e9ff] object-cover',
    size === 'lg' ? 'size-16 rounded-[6px]' : 'size-[42px] rounded-[2px]',
  )
  const fallbackClassName = cn(
    'inline-flex shrink-0 items-center justify-center bg-[#e2e9ff] font-semibold text-[var(--color-primary)]',
    size === 'lg' ? 'size-16 rounded-[6px] text-xl' : 'size-[42px] rounded-[2px] text-base',
  )

  if (app.avatar) {
    return <img alt="" className={avatarClassName} src={app.avatar} />
  }

  return <span className={fallbackClassName}>{initial}</span>
}

function AppCard({
  app,
  busy,
  installPending,
  onInstall,
  onOpenDetails,
  onPrimaryAction,
  onRequestRemove,
  onToggleFavorite,
  showFavorite,
  showRemove,
}: {
  app: UserApp
  busy: boolean
  installPending: boolean
  onInstall: (app: UserApp) => void
  onOpenDetails: (app: UserApp) => void
  onPrimaryAction?: (app: UserApp) => void
  onRequestRemove: (app: UserApp) => void
  onToggleFavorite: (app: UserApp) => void
  showFavorite: boolean
  showRemove: boolean
}) {
  const { t } = useTranslation('userApps')
  const tags = (app.tags ?? []).slice(0, 2)
  const showInstallAction = !app.is_installed
  const showRemoveAction = showRemove && app.is_installed
  const summaryClassName = cn(
    'flex min-w-0 items-start gap-3 text-left',
    showFavorite ? 'pr-8' : null,
    onPrimaryAction
      ? 'cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]'
      : null,
  )
  const summaryContent = (
    <>
      <AppAvatar app={app} />
      <div className="min-w-0">
        <h3 className="truncate text-base leading-5 font-bold text-[var(--color-text)]">
          {app.name}
        </h3>
        <div className="mt-1 flex min-h-5 flex-wrap gap-1">
          {tags.map((tag) => (
            <Badge className="h-[18px] rounded px-1.5 text-[10px] font-normal" key={tag.id}>
              {tag.name}
            </Badge>
          ))}
          {(app.tags?.length ?? 0) > tags.length ? (
            <Badge className="h-[18px] rounded px-1.5 text-[10px] font-normal">
              {t('moreTags')}
            </Badge>
          ) : null}
        </div>
      </div>
    </>
  )
  const description = app.description || t('emptyDescription')

  return (
    <article className="user-app-card group relative flex min-h-[168px] flex-col rounded-lg border border-[var(--color-panel-border)] bg-white p-4 shadow-[0_0_0_rgba(26,28,25,0)] transition-shadow hover:shadow-[0_4px_14px_rgba(26,28,25,0.08)]">
      {showFavorite ? (
        <button
          aria-label={
            app.is_favorite
              ? t('actions.unfavorite', { name: app.name })
              : t('actions.favorite', { name: app.name })
          }
          className={cn(
            'absolute top-3 right-3 inline-flex size-8 cursor-pointer items-center justify-center rounded-md transition-opacity duration-150 hover:bg-[rgba(26,28,25,0.05)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
            app.is_favorite
              ? 'opacity-100'
              : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100',
          )}
          disabled={busy}
          onClick={(event) => {
            event.stopPropagation()
            onToggleFavorite(app)
          }}
          type="button"
        >
          <PinIcon filled={app.is_favorite} />
        </button>
      ) : null}
      {onPrimaryAction ? (
        <button
          className={summaryClassName}
          onClick={() => {
            onPrimaryAction(app)
          }}
          type="button"
        >
          {summaryContent}
        </button>
      ) : (
        <div className={summaryClassName}>{summaryContent}</div>
      )}
      {onPrimaryAction ? (
        <button
          className="mt-3 line-clamp-2 min-h-9 cursor-pointer text-left text-xs leading-[18px] text-[#8c8e90] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]"
          onClick={() => {
            onPrimaryAction(app)
          }}
          type="button"
        >
          {description}
        </button>
      ) : (
        <p className="mt-3 line-clamp-2 min-h-9 text-left text-xs leading-[18px] text-[#8c8e90]">
          {description}
        </p>
      )}
      <div className="mt-auto flex min-h-8 items-end justify-between gap-2 pt-3">
        <div className="min-w-0 truncate pr-2 text-xs leading-[18px] text-[#8c8e90]">
          {app.version ? `v${app.version.replace(/^v/i, '')}` : t('emptyVersion')}
        </div>
        {/* 宽度需容纳「添加」按钮 loading 态的圆点 + 间距，避免文字与圆点争夺空间。 */}
        <div className="user-app-card-actions grid w-[140px] shrink-0 grid-cols-2 items-center gap-2 transition-opacity duration-150">
          <Button
            aria-label={t('actions.details', { name: app.name })}
            className="h-8 w-full rounded-md px-2 text-xs font-medium"
            onClick={(event) => {
              event.stopPropagation()
              onOpenDetails(app)
            }}
            size="sm"
            variant="soft"
          >
            {t('actions.detailsShort')}
          </Button>
          {showRemoveAction ? (
            <Button
              aria-label={t('actions.removeNamed', { name: app.name })}
              className="h-8 w-full rounded-md px-2 text-xs font-medium"
              disabled={busy}
              onClick={(event) => {
                event.stopPropagation()
                onRequestRemove(app)
              }}
              size="sm"
              variant="dangerSoft"
            >
              {t('actions.removeShort')}
            </Button>
          ) : showInstallAction ? (
            <Button
              aria-label={t('actions.installNamed', { name: app.name })}
              className="h-8 w-full rounded-md px-2 text-xs font-medium"
              isLoading={installPending}
              onClick={(event) => {
                event.stopPropagation()
                onInstall(app)
              }}
              size="sm"
            >
              {t('actions.installShort')}
            </Button>
          ) : (
            <span aria-hidden />
          )}
        </div>
      </div>
    </article>
  )
}

function AppGridSkeleton() {
  return (
    <div className="user-app-grid-enter grid grid-cols-[repeat(auto-fill,minmax(236px,1fr))] gap-3">
      {Array.from({ length: 8 }).map((_, index) => (
        <Skeleton className="app-list-skeleton-item h-[168px] rounded-lg" key={index} />
      ))}
    </div>
  )
}

function EmptyAppsState({
  actionLabel,
  message,
  onAction,
}: {
  actionLabel?: string
  message: string
  onAction?: () => void
}) {
  return (
    <div className="flex min-h-[420px] flex-col items-center justify-center text-center">
      <div className="relative h-10 w-16" aria-hidden>
        <img alt="" className="absolute top-[26px] left-0 h-3.5 w-16" src={emptyOvalUrl} />
        <img
          alt=""
          className="absolute top-0 left-[9px] h-[23px] w-[46px]"
          src={emptyBoxShapeUrl}
        />
        <img
          alt=""
          className="absolute top-[13px] left-[9px] h-[23px] w-[46px]"
          src={emptyBoxCombinedUrl}
        />
      </div>
      <p className="mt-5 text-sm leading-5 text-[var(--color-text)]">{message}</p>
      {actionLabel && onAction ? (
        <button
          className="mt-4 h-[38px] cursor-pointer rounded-lg bg-[var(--color-primary)] px-4 text-sm text-white hover:bg-[var(--color-primary-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
          onClick={onAction}
          type="button"
        >
          {actionLabel}
        </button>
      ) : null}
    </div>
  )
}

export function UserAppsPage() {
  const isMobile = useMediaQuery('(max-width: 767px)')
  if (isMobile) return <AppsMobilePage />

  return <AppsDesktopPage />
}

function AppsDesktopPage() {
  const {
    activeQuery,
    activeTab,
    apps,
    categoryItems,
    favoriteMutation,
    handleUseApp,
    hasActiveFilter,
    installMutation,
    isBusy,
    isInitialLoading,
    operationError,
    pendingRemoveApp,
    requestRemoveApp,
    searchInput,
    selectedApp,
    setActiveTab,
    setPendingRemoveApp,
    setSearchInput,
    setSelectedApp,
    setSelectedTagId,
    showGridSkeleton,
    t,
    uninstallMutation,
    validatedTagId,
  } = useAppsPage()

  return (
    <UserShellPage activePath="/apps">
      <div className="flex h-full flex-col overflow-hidden px-4 py-5 sm:px-6 lg:px-8">
        <header className="flex shrink-0 flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-6">
              <button
                className={cn(
                  'cursor-pointer text-xl leading-7 font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]',
                  activeTab === 'mine' ? 'text-[var(--color-primary)]' : 'text-[var(--color-text)]',
                )}
                onClick={() => {
                  setActiveTab('mine')
                }}
                type="button"
              >
                {t('tabs.mine')}
              </button>
              <button
                className={cn(
                  'cursor-pointer text-xl leading-7 font-bold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-focus)]',
                  activeTab === 'market'
                    ? 'text-[var(--color-primary)]'
                    : 'text-[var(--color-text)]',
                )}
                onClick={() => {
                  setActiveTab('market')
                }}
                type="button"
              >
                {t('tabs.market')}
              </button>
            </div>
            <div className="mt-4 flex max-w-full gap-1 overflow-x-auto pb-1">
              {categoryItems.map((item) => (
                <button
                  className={cn(
                    'h-8 shrink-0 cursor-pointer rounded px-4 text-sm font-bold whitespace-nowrap focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                    validatedTagId === item.id
                      ? 'bg-[var(--color-primary)] text-white'
                      : 'bg-transparent text-[var(--color-text)] hover:bg-[rgba(26,28,25,0.05)]',
                  )}
                  key={item.id || 'all'}
                  onClick={() => {
                    setSelectedTagId(item.id)
                  }}
                  type="button"
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>
          <SearchInput
            aria-label={t('search.label')}
            className="h-9 w-full rounded-lg border-[var(--color-control-border)] shadow-[var(--shadow-control)] xl:w-[274px]"
            onChange={(event) => {
              setSearchInput(event.target.value)
            }}
            placeholder={t('search.placeholder')}
            value={searchInput}
          />
        </header>

        {operationError ? (
          <div className="mt-4 shrink-0">
            <ErrorState
              description={appErrorCopy(
                operationError,
                t('errors.operationDescription'),
                t('errors.unauthorized'),
                t('errors.forbidden'),
                t('errors.channelUnavailable'),
              )}
              title={t('errors.operationTitle')}
            />
          </div>
        ) : null}

        <div className="mt-5 min-h-0 flex-1 overflow-auto pb-8">
          {isInitialLoading ? (
            <>
              <div className="app-list-progress" aria-hidden />
              <div className="sr-only" role="status">
                {t('loading')}
              </div>
            </>
          ) : null}
          {showGridSkeleton ? <AppGridSkeleton /> : null}
          {activeQuery.isError && !activeQuery.data ? (
            <ErrorState
              description={appErrorCopy(
                activeQuery.error,
                t('errors.listDescription'),
                t('errors.unauthorized'),
                t('errors.forbidden'),
                t('errors.channelUnavailable'),
              )}
              onRetry={() => {
                void activeQuery.refetch()
              }}
              title={t('errors.listTitle')}
            />
          ) : null}
          {!isInitialLoading && !activeQuery.isError && apps.length === 0 ? (
            hasActiveFilter ? (
              <EmptyAppsState message={t('empty.filtered')} />
            ) : activeTab === 'mine' ? (
              <EmptyAppsState
                actionLabel={t('empty.goMarket')}
                message={t('empty.mine')}
                onAction={() => {
                  setSearchInput('')
                  setSelectedTagId('')
                  setActiveTab('market')
                }}
              />
            ) : (
              <EmptyAppsState message={t('empty.market')} />
            )
          ) : null}
          {apps.length > 0 ? (
            <div className="user-app-grid-enter grid grid-cols-[repeat(auto-fill,minmax(236px,1fr))] gap-3">
              {apps.map((app) => (
                <AppCard
                  app={app}
                  busy={isBusy}
                  installPending={installMutation.isPending}
                  key={app.id}
                  onInstall={(nextApp) => {
                    installMutation.mutate(nextApp.id)
                  }}
                  onOpenDetails={setSelectedApp}
                  onPrimaryAction={
                    activeTab === 'mine'
                      ? (nextApp) => {
                          void handleUseApp(nextApp)
                        }
                      : undefined
                  }
                  onToggleFavorite={(nextApp) => {
                    favoriteMutation.mutate(nextApp)
                  }}
                  onRequestRemove={requestRemoveApp}
                  showFavorite={activeTab === 'mine'}
                  showRemove={activeTab === 'mine'}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        className="max-w-[480px] rounded-2xl"
        closeLabel={t('detail.close')}
        footer={
          selectedApp ? (
            selectedApp.is_installed ? (
              <>
                <Button
                  className="flex-1"
                  onClick={() => {
                    requestRemoveApp(selectedApp)
                  }}
                  variant="dangerSoft"
                >
                  {t('actions.remove')}
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    void handleUseApp(selectedApp)
                  }}
                >
                  {t('actions.use')}
                </Button>
              </>
            ) : (
              <Button
                className="w-full"
                isLoading={installMutation.isPending}
                onClick={() => {
                  installMutation.mutate(selectedApp.id)
                }}
              >
                {t('actions.install')}
              </Button>
            )
          ) : null
        }
        onClose={() => {
          setSelectedApp(null)
        }}
        open={selectedApp !== null}
        title={selectedApp?.name ?? ''}
        titleVisuallyHidden
      >
        {selectedApp ? (
          <div className="space-y-4 pt-10">
            <div className="flex flex-col items-center gap-2 text-center">
              <AppAvatar app={selectedApp} size="lg" />
              <p className="text-base leading-6 font-bold text-[var(--color-text)]">
                {selectedApp.name}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-1">
                {(selectedApp.tags ?? []).slice(0, 3).map((tag) => (
                  <Badge className="h-[18px] rounded px-1.5 text-[10px] font-normal" key={tag.id}>
                    {tag.name}
                  </Badge>
                ))}
                {selectedApp.version ? (
                  <>
                    {(selectedApp.tags?.length ?? 0) > 0 ? (
                      <span className="mx-1 h-[18px] w-px bg-[#d8d8d8]" aria-hidden />
                    ) : null}
                    <span className="text-xs leading-[18px] text-[#8c8e90]">
                      v{selectedApp.version.replace(/^v/i, '')}
                    </span>
                  </>
                ) : null}
              </div>
            </div>
            <p className="text-xs leading-[18px] text-[#8c8e90]">
              {selectedApp.description || t('emptyDescription')}
            </p>
            {selectedApp.usage_instructions ? (
              <div>
                <p className="text-xs leading-[18px] text-[var(--color-text)]">
                  {t('detail.instructions')}
                </p>
                <p className="mt-2 whitespace-pre-line text-sm leading-6 text-[var(--color-text)]">
                  {selectedApp.usage_instructions}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}
      </Modal>
      <Modal
        className="max-w-[400px]"
        closeLabel={t('removeConfirm.close')}
        footer={
          <>
            <Button
              className="flex-1"
              onClick={() => {
                setPendingRemoveApp(null)
              }}
              variant="secondary"
            >
              {t('removeConfirm.cancel')}
            </Button>
            <Button
              className="flex-1"
              isLoading={uninstallMutation.isPending}
              onClick={() => {
                if (pendingRemoveApp) {
                  uninstallMutation.mutate(pendingRemoveApp.id)
                }
              }}
              variant="danger"
            >
              {t('removeConfirm.confirm')}
            </Button>
          </>
        }
        onClose={() => {
          setPendingRemoveApp(null)
        }}
        open={pendingRemoveApp !== null}
        title={t('removeConfirm.title')}
      >
        <p className="text-sm leading-6 text-[var(--color-text-muted)]">
          {t('removeConfirm.description', { name: pendingRemoveApp?.name ?? '' })}
        </p>
      </Modal>
    </UserShellPage>
  )
}
